// @ts-strict-ignore
import { component } from "picoapp";
import { PRODUCT_CARD_MAIN_SETTINGS } from "../../sections/Carousels/tbsp-carousel--constants";
import { InnerArrows } from "../Options/tbsp-product-options-carousel";
import {
  type Components,
  EventInterface,
  type SlideComponent,
  type Splide,
} from "@splidejs/splide";
import { updateMaestroVariants } from "../../utilities";
import { type Context } from "../../state";

export function ProductCardSlider(
  syncSlider: Splide,
  callback?: (slide?: SlideComponent) => void
) {
  return function (Splide: Splide, Components: Components) {
    const { on } = EventInterface(Splide);
    const { Controller, Slides } = Components;
    const setAriaCurrent = () => {
      const index = Controller.getIndex();
      const activeSlide = Slides.getAt(index);
      if (!activeSlide) return;
      if (activeSlide.slide.hasAttribute("data-arrow")) return;
      Slides.forEach(({ slide }) => {
        slide.removeAttribute("aria-current");
      });
      activeSlide.slide.setAttribute("aria-current", "true");
    };

    function mount() {
      on("active", setAriaCurrent);

      const productOptionSlides = Slides.filter(({ slide }) => {
        if (!slide.getAttribute("data-arrow")) {
          return true;
        }
        return false;
      });

      productOptionSlides.forEach((slide, index) => {
        if (slide.slide.hasAttribute("data-arrow")) return;
        slide.slide.addEventListener("mouseenter", () => {
          Controller.setIndex(slide.index);
          Splide.emit("active");
          syncSlider.go(index);

          if (callback) {
            callback(slide);
          }
        });
      });

      Splide.refresh();
    }

    return { mount };
  };
}

export const productCardCarousel = component<Context>(async (node, context) => {
  const hasOnlyDefaultVariant = node.dataset.hasOnlyDefaultVariant === "true";
  const isLegacyProductBundle = node.dataset.isProductBundle === "true";

  if (hasOnlyDefaultVariant || isLegacyProductBundle) {
    return;
  }

  const [{ default: Splide }] = await Promise.all([
    import("@splidejs/splide"),
    import("@splidejs/splide/css/core"),
  ]);

  const imageSlider = node.querySelector<HTMLElement>("aside");
  const optionsSlider = node.querySelector<HTMLElement>("nav");

  if (!imageSlider || !optionsSlider) {
    return;
  }

  let start = 0;
  const initialSlide = imageSlider.querySelector<HTMLElement>(
    `.splide__slide[data-selected]`
  );
  if (initialSlide && initialSlide.dataset.index)
    start = parseInt(initialSlide.dataset.index);

  const images = new Splide(imageSlider, {
    ...PRODUCT_CARD_MAIN_SETTINGS,
    start,
  });

  images.on("visible", ({ slide }) => {
    slide.classList.remove("absolute", "invisible");
  });

  images.on("move", function (newSlideIndex) {
    const prices = node.querySelectorAll("[data-prices]");
    prices.forEach((price) => {
      if (price instanceof HTMLElement) {
        price.style.display = "none";
      }
    });
    const activePriceElement = node.querySelector<HTMLElement>(
      `[data-prices][data-index="${newSlideIndex}"]`
    );
    if (activePriceElement) {
      activePriceElement.style.display = "flex";
    }
  });

  const breakpoints = {
    376: {
      ...(optionsSlider.dataset.smPerPage && {
        perPage: parseInt(optionsSlider.dataset.smPerPage),
      }),
      ...(optionsSlider.dataset.smGap && {
        gap: parseInt(optionsSlider.dataset.smGap),
      }),
      ...(optionsSlider.dataset.smPadding && {
        padding: parseInt(optionsSlider.dataset.smPadding),
      }),
    },
    768: {
      ...(optionsSlider.dataset.mdPerPage && {
        perPage: parseInt(optionsSlider.dataset.mdPerPage),
      }),
      ...(optionsSlider.dataset.mdGap && {
        gap: parseInt(optionsSlider.dataset.mdGap),
      }),
      ...(optionsSlider.dataset.mdPadding && {
        padding: parseInt(optionsSlider.dataset.mdPadding),
      }),
    },
    1024: {
      ...(optionsSlider.dataset.mgPerPage && {
        perPage: parseInt(optionsSlider.dataset.mgPerPage),
      }),
      ...(optionsSlider.dataset.mgGap && {
        gap: parseInt(optionsSlider.dataset.mgGap),
      }),
      ...(optionsSlider.dataset.mgPadding && {
        padding: parseInt(optionsSlider.dataset.mgPadding),
      }),
    },
    1280: {
      ...(optionsSlider.dataset.lgPerPage && {
        perPage: parseInt(optionsSlider.dataset.lgPerPage),
      }),
      ...(optionsSlider.dataset.lgGap && {
        gap: parseInt(optionsSlider.dataset.lgGap),
      }),
      ...(optionsSlider.dataset.lgPadding && {
        padding: parseInt(optionsSlider.dataset.lgPadding),
      }),
    },
    1440: {
      ...(optionsSlider.dataset.xlPerPage && {
        perPage: parseInt(optionsSlider.dataset.xlPerPage),
      }),
      ...(optionsSlider.dataset.xlGap && {
        gap: parseInt(optionsSlider.dataset.xlGap),
      }),
      ...(optionsSlider.dataset.xlPadding && {
        padding: parseInt(optionsSlider.dataset.xlPadding),
      }),
    },
  };

  const productOptions = new Splide(optionsSlider, {
    type: "slide",
    rewind: true,
    clones: 0,
    arrows: false,
    pagination: false,
    drag: false,
    start,
    mediaQuery: "min",
    perPage: parseInt(optionsSlider.dataset.perPage || "0"),
    padding: parseInt(optionsSlider.dataset.padding || "0"),
    gap: parseInt(optionsSlider.dataset.gap || "0"),
    breakpoints,
  });

  const updateMaestroAttributes = ({ slide }: SlideComponent) => {
    const swatch = slide.querySelector<HTMLDivElement>(
      "[data-product-variant-id]"
    );
    if (!swatch) return;

    const productCard = swatch.closest<HTMLElement>(
      "[data-product-selected-variant-id]"
    );
    if (productCard) {
      updateMaestroVariants(productCard, swatch);
      productCard.dataset.productSelectedVariantId =
        swatch.dataset.productVariantId || "";
      productCard.dataset.productSelectedVariantShippingNote =
        swatch.dataset.productVariantShippingNote || "";
    }
  };

  productOptions.mount({
    InnerArrows: InnerArrows(
      node.dataset.nextLabel || "",
      node.dataset.prevLabel || "",
      true
    ),
    ProductCardSlider: ProductCardSlider(images, (slide) => {
      if (slide) {
        updateMaestroAttributes(slide);
      }
    }),
  });
  images.mount();
  optionsSlider.classList.replace("opacity-0", "opacity-100");

  context.on("tbsp:variant-selected", async ({ selectedVariant }) => {
    if (selectedVariant.supplementalProduct) {
      return;
    }

    const matchingOption = node.querySelector<HTMLDivElement>(
      `nav [data-product-variant-title="${selectedVariant.title}"]`
    );

    if (matchingOption) {
      matchingOption.dispatchEvent(new Event("mouseenter"));
    }
  });
});
